// Playback-speed preference for the audio players (SPEED task): persisted in localStorage
// (same try/catch-and-ignore pattern as ../lib/uploadStorage.ts — this is a single small
// primitive value, never audio/transcript content, so it's fine to persist client-side) so the
// chosen speed sticks across recordings and sessions rather than resetting to 1x every time a
// new recording is opened.
import { useCallback, useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'scribe.playback-rate.v1';

/** The only selectable speeds — kept narrow (rather than a free `number`) so a corrupted or
 * hand-edited localStorage value can't set a nonsensical rate; `readStoredRate` below falls
 * back to `DEFAULT_PLAYBACK_RATE` for anything outside this set. */
export const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2] as const;
export type PlaybackRate = (typeof PLAYBACK_RATES)[number];
export const DEFAULT_PLAYBACK_RATE: PlaybackRate = 1;

function isPlaybackRate(value: number): value is PlaybackRate {
  return (PLAYBACK_RATES as readonly number[]).includes(value);
}

function readStoredRate(): PlaybackRate {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? Number(raw) : NaN;
    return isPlaybackRate(parsed) ? parsed : DEFAULT_PLAYBACK_RATE;
  } catch {
    return DEFAULT_PLAYBACK_RATE;
  }
}

function writeStoredRate(rate: PlaybackRate): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(rate));
  } catch {
    // Quota or serialization failure — non-fatal; the in-memory preference keeps working for
    // this session, it just won't survive a reload. Never throw from storage.
  }
}

/**
 * Returns the persisted playback-speed preference plus:
 *  - `setRate`: updates state, persists to localStorage, and applies to the currently bound
 *    `<audio>` element (if any) immediately.
 *  - `bindAudio`: a ref callback for the `<audio>` element. Applies `playbackRate` the moment
 *    the element (re)mounts — covering both a real unmount/remount (e.g. RecordingDetail's
 *    `AudioSection`, which conditionally renders a fresh `<audio>` across
 *    loading/error/available states) and a source swap on an element that stays mounted (e.g.
 *    `TranscriptPlayer` switching to a different recording's `audioUrl`) — so the chosen speed
 *    never silently resets to the browser default of 1x.
 *
 * One hook instance == one bound element at a time. `TranscriptPlayer` and `AudioSection`
 * never render simultaneously (RecordingDetail picks one or the other), so each reads/writes
 * the same storage key independently without stepping on the other.
 */
export function usePlaybackRate(): {
  rate: PlaybackRate;
  setRate: (rate: PlaybackRate) => void;
  bindAudio: (el: HTMLAudioElement | null) => void;
} {
  const [rate, setRateState] = useState<PlaybackRate>(readStoredRate);
  const boundElRef = useRef<HTMLAudioElement | null>(null);

  const setRate = useCallback((next: PlaybackRate) => {
    setRateState(next);
    writeStoredRate(next);
    if (boundElRef.current) boundElRef.current.playbackRate = next;
  }, []);

  // Re-apply to the currently bound element whenever the rate changes for any other reason
  // (kept separate from setRate's own immediate assignment above for the common case, this
  // covers a future caller driving `rate` from elsewhere).
  useEffect(() => {
    if (boundElRef.current) boundElRef.current.playbackRate = rate;
  }, [rate]);

  const bindAudio = useCallback(
    (el: HTMLAudioElement | null) => {
      boundElRef.current = el;
      if (el) el.playbackRate = rate;
    },
    [rate],
  );

  return { rate, setRate, bindAudio };
}
